import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import { keepPreviousData } from "@tanstack/react-query";
import styled from "@emotion/styled";
import {
  Theme,
  Typography,
  useMediaQuery,
  TableContainer,
} from "@mui/material";
import { Booking } from "@bn/api/apiv1";
import { useInfiniteListAndSearchAllBookings } from "@api/v1/hooks/useBookings";
import { useRangeSelectControl, useSecurityImages, useTableSort } from "@hooks";
import {
  AutoTable,
  LoadingPlaceHolder,
  Table,
  TableSummary,
} from "@components";
import { BOOKINGS_COMPATIBILITY_VERSION } from "@constants";
import { EditBookingModal } from "@features/investments/investment/components/InvestmentTable/InvestmentDetailModal/EditBookingModal";
import { DeleteBookingDialog } from "@features/investments/investment/components/InvestmentTable/InvestmentDetailModal/DeleteBookingDialog";
import { BookingsTableBody } from "./BookingsTableBody";
import { useBookingsTableConfig } from "./useBookingsTableConfig";

const PER_PAGE = 50;

const NoItemsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

interface BookingsTableProps {
  summaryValue: number;
  accountIds: number[];
  rangeData: ReturnType<typeof useRangeSelectControl>;
  /** Set on the single-depot page so investment links keep the depot context. */
  depotId?: number;
}

export const BookingsTableContainer = (props: BookingsTableProps) => {
  const { summaryValue, rangeData, accountIds = [], depotId } = props;
  const { range, intervalType, customIntervalSelect, selectInterval } =
    rangeData;

  const isMoreSM = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));

  const { t } = useTranslation("");

  const { sort, sortField, sortDirection } = useTableSort(
    { field: "valueDate", direction: "DESC" },
    "investmentsBookingsTable"
  );

  const order =
    sortField && sortDirection
      ? [`${sortField} ${sortDirection.toLowerCase()}`]
      : undefined;

  const {
    data,
    isPending,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteListAndSearchAllBookings(
    {
      accountId: accountIds,
      minDate: range?.startDate?.toISOString(),
      maxDate: range?.endDate.toISOString(),
      enumCompatibilityVersion: BOOKINGS_COMPATIBILITY_VERSION,
      order,
      perPage: PER_PAGE,
    },
    {
      enabled: accountIds.length > 0,
      initialPageParam: 1,
      placeholderData: keepPreviousData,
      getNextPageParam: (lastPage) => {
        const page = lastPage.paging?.page ?? 0;
        const pageCount = lastPage.paging?.pageCount ?? 0;
        return page < pageCount ? page + 1 : undefined;
      },
    }
  );

  const bookings: Booking[] = useMemo(
    () => (data?.pages ?? []).flatMap((p) => p.bookings ?? []),
    [data]
  );

  const config = useBookingsTableConfig({
    sortField,
    sortDirection,
    onSort: sort,
  });

  const logoSecurity = useMemo(
    () =>
      bookings.map((booking) => ({
        hasValidIsin: true,
        type:
          booking.investment.quoteProvider === "currency"
            ? "81_fcurr"
            : "11_stock",
        isin: booking.investment.isin || booking.investment.tickerSymbol,
        ticker_symbol: booking.investment.tickerSymbol,
        quoteProvider: booking.investment.quoteProvider,
      })),
    [bookings]
  );

  const logos = useSecurityImages(logoSecurity);

  const [editingBooking, setEditingBooking] = useState<Booking>();
  const [deletingBooking, setDeletingBooking] = useState<Booking>();

  const handleEditBooking = useCallback((booking: Booking) => {
    setEditingBooking(booking);
  }, []);

  const handleDeleteBooking = useCallback((booking: Booking) => {
    setDeletingBooking(booking);
  }, []);

  const additionalProps = useMemo(
    () => ({
      logos,
      onEditBooking: handleEditBooking,
      onDeleteBooking: handleDeleteBooking,
      depotId,
    }),
    [logos, handleEditBooking, handleDeleteBooking, depotId]
  );

  const editingImage = editingBooking
    ? logos[editingBooking.investment.isin || ""] ||
      logos[editingBooking.investment.tickerSymbol || ""]
    : undefined;

  if (isPending && accountIds.length > 0) {
    return <LoadingPlaceHolder />;
  }

  const loader = isFetchingNextPage ? <LoadingPlaceHolder /> : null;

  return (
    <div>
      <TableContainer>
        <TableSummary
          summaryValue={summaryValue}
          currencySymbol="EUR"
          intervalType={intervalType}
          range={range}
          onSelectInterval={selectInterval}
          onSelectCustomInterval={customIntervalSelect}
        />

        {isMoreSM ? (
          <AutoTable
            settings={config}
            data={bookings}
            additionalProps={additionalProps}
            isFetching={isFetching && !isFetchingNextPage}
            infiniteScroll={{
              hasMore: Boolean(hasNextPage),
              next: () => {
                fetchNextPage();
              },
              loader,
            }}
            showNoData={bookings.length === 0}
            noDataFirstLine={t(
              "components.depotsStatistic.bookingsTable.noItems"
            )}
          />
        ) : (
          <InfiniteScroll
            dataLength={bookings.length}
            next={() => {
              fetchNextPage();
            }}
            hasMore={Boolean(hasNextPage)}
            loader={loader}
          >
            <Table>
              <BookingsTableBody data={bookings} depotId={depotId} />
            </Table>
            {!isFetching && !bookings.length && (
              <NoItemsContainer>
                <Typography variant="h5" color="textPrimary">
                  {t("components.depotsStatistic.bookingsTable.noItems")}
                </Typography>
              </NoItemsContainer>
            )}
          </InfiniteScroll>
        )}
      </TableContainer>

      <EditBookingModal
        booking={editingBooking}
        investmentId={editingBooking?.investment.id ?? 0}
        image={editingImage}
        isOpen={!!editingBooking}
        onClose={() => setEditingBooking(undefined)}
        onSuccess={() => setEditingBooking(undefined)}
      />

      {deletingBooking && (
        <DeleteBookingDialog
          bookingId={deletingBooking.id}
          isOpen
          onClose={() => setDeletingBooking(undefined)}
        />
      )}
    </div>
  );
};
